// store/notificationsSlice.ts - Updated with refresh triggers
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from 'api/notificationAPI';
import { createRequest } from 'store/allRequestsSlice'; // ADDED
import { approveRequest, rejectRequest } from 'store/approvalsSlice'; // ADDED

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  related_entity_type: string;
  related_entity_id: number;
  user_id: number;
  is_read: boolean;
  created_at: string;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (unreadOnly: boolean = false) => {
    const response = await getNotifications(unreadOnly);
    return response;
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: number) => {
    await markNotificationAsRead(notificationId);
    return notificationId;
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async () => {
    await markAllNotificationsAsRead();
    return null;
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    // ADDED: Manual refresh action
    refreshNotifications: state => {
      state.loading = true;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter(
          notification => !notification.is_read
        ).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find(
          n => n.id === notificationId
        );
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      // Mark all as read
      .addCase(markAllAsRead.fulfilled, state => {
        state.notifications.forEach(notification => {
          notification.is_read = true;
        });
        state.unreadCount = 0;
      })
      // ADDED: Refresh notifications when requests are created or processed
      .addCase(createRequest.fulfilled, state => {
        // Increment unread count when new request is created
        state.unreadCount += 1;
      })
      .addCase(approveRequest.fulfilled, state => {
        // Increment unread count when request is approved
        state.unreadCount += 1;
      })
      .addCase(rejectRequest.fulfilled, state => {
        // Increment unread count when request is rejected
        state.unreadCount += 1;
      });
  }
});

export const { clearError, refreshNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;

// Selectors
export const selectNotifications = (state: {
  notifications: NotificationsState;
}) => state.notifications.notifications;
export const selectUnreadCount = (state: {
  notifications: NotificationsState;
}) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state: {
  notifications: NotificationsState;
}) => state.notifications.loading;
export const selectNotificationsError = (state: {
  notifications: NotificationsState;
}) => state.notifications.error;
